/**
 * Edward Jones Quick Plan (`edj-minimal-v1`): the rules the server and the client must agree on.
 *
 * An EDJ advisor's Quick Plan collects DOB, sex, ZIP, Married / Not married and net worth, and the
 * server sends the models every survey key as null except those answers, built by
 * `buildEdjQuickPlanModelInputV1` (packages/server/src/util/modelInputs). This file holds only what
 * both sides need:
 *
 * - `resolveQuickPlanAvailability` decides which short-form profile an advisor may use. The server
 *   (`util/resolveShortFormAccess.ts`) refuses any request that does not match it, and the client's
 *   Quick Plan entry points read the same answer, so the UI and the API cannot disagree.
 * - `isEdjQuickEstimate` decides whether a result is shown as a "Quick estimate". It reads the
 *   provenance recorded on the applied (latest) inference row, never client metadata, so a later
 *   full intake clears it without anything else changing.
 * - `usesEdjQuickPlanModelInput` decides whether re-runs use the EDJ input (server) and which
 *   quick-edit fields are offered (client).
 * - The answer checks (`edjQuickPlanAnswersProblem` and the predicates and messages it is built
 *   from) are shared by the create form, the quick-edit form, the server's request and edit
 *   validation, and the model body builder, so an answer that is saved can always be sent to the
 *   models. `edjQuickPlanEditProblem` applies them to an edit: the stored answers with the change
 *   applied, checked before anything is written.
 * - Marital status is editable only on an EDJ quick estimate (`EDJ_QUICK_EDIT_EXTRA_FIELDS`); the
 *   standard quick edit never offered it. `edjQuickPlanPartneredAnswer` keeps `householdPartnered`
 *   in step with it on create and on edit.
 *
 * The `edjQuickPlan` AppConfig flag gates EDJ access. It is resolved per advisor with the EDJ
 * organization bucket applied, so it serves both the staged rollout and the kill switch.
 */
import { PREFILL_FIELD_RULES } from './advisorPrefillOptions';
import { findActivePilotProgram } from './pilotPrograms';
import { zipCodeToState } from './zipCodeToState';

export type NamedCensusRegion = Exclude<CensusRegion, 'Other'>;

export const EDJ_QUICK_PLAN_PROFILE = 'edj-minimal-v1' as const satisfies ShortFormIntakeProfile &
  ModelInputProfile;
export const STANDARD_SHORT_FORM_PROFILE = 'standard' as const satisfies ShortFormIntakeProfile;
export const STANDARD_MODEL_INPUT_PROFILE = 'standard-survey-v1' as const satisfies ModelInputProfile;
export const EDJ_QUICK_PLAN_CONTRACT_VERSION = 1;
export const EDJ_QUICK_PLAN_FEATURE_FLAG = 'edjQuickPlan' as const satisfies AppConfigFeatureFlagKey;

export const SHORT_FORM_INTAKE_PROFILES: readonly ShortFormIntakeProfile[] = [
  STANDARD_SHORT_FORM_PROFILE,
  EDJ_QUICK_PLAN_PROFILE,
];

export const EDJ_QUICK_PLAN_MARITAL_CATEGORIES = [
  'Married',
  'Not married',
] as const satisfies readonly EdjQuickPlanMaritalCategory[];

/** The two sex values the EDJ model input accepts. */
export const EDJ_QUICK_PLAN_SEX_OPTIONS = ['Male', 'Female'] as const;
export type EdjQuickPlanSex = (typeof EDJ_QUICK_PLAN_SEX_OPTIONS)[number];

export const EDJ_QUICK_PLAN_NET_WORTH_MIN = PREFILL_FIELD_RULES.householdTotalAssetsValue.min;
export const EDJ_QUICK_PLAN_NET_WORTH_MAX = PREFILL_FIELD_RULES.householdTotalAssetsValue.max;
export const EDJ_QUICK_PLAN_MIN_BIRTH_YEAR = PREFILL_FIELD_RULES.clientBirthDate.minYear;

/** The messages every EDJ check uses, so the form, the API and the model input say the same thing. */
export const EDJ_QUICK_PLAN_MESSAGES = {
  birthDateInvalid: 'Enter a valid date of birth.',
  birthDateFuture: 'Date of birth cannot be in the future.',
  birthDateTooEarly: `Date of birth must be in ${EDJ_QUICK_PLAN_MIN_BIRTH_YEAR} or later.`,
  sex: `Sex must be ${EDJ_QUICK_PLAN_SEX_OPTIONS.join(' or ')}.`,
  maritalStatus: `Marital status must be ${EDJ_QUICK_PLAN_MARITAL_CATEGORIES.join(' or ')}.`,
  zipCode: 'Enter a US zip code we recognise.',
  netWorth: `Net worth must be between $${EDJ_QUICK_PLAN_NET_WORTH_MIN.toLocaleString('en-US')} and $${EDJ_QUICK_PLAN_NET_WORTH_MAX.toLocaleString('en-US')}.`,
} as const;

export function isEdjQuickPlanSex(value: unknown): value is EdjQuickPlanSex {
  return EDJ_QUICK_PLAN_SEX_OPTIONS.includes(value as EdjQuickPlanSex);
}

/** Null when the birth date is usable: a real date, not in the future, and not before 1900. */
export function edjQuickPlanBirthDateProblem(value: unknown, now: Date = new Date()): string | null {
  const birthDate =
    value instanceof Date ? value : typeof value === 'string' ? new Date(value) : null;
  if (!birthDate || isNaN(birthDate.getTime())) return EDJ_QUICK_PLAN_MESSAGES.birthDateInvalid;
  if (birthDate.getTime() > now.getTime()) return EDJ_QUICK_PLAN_MESSAGES.birthDateFuture;
  if (birthDate.getUTCFullYear() < EDJ_QUICK_PLAN_MIN_BIRTH_YEAR) {
    return EDJ_QUICK_PLAN_MESSAGES.birthDateTooEarly;
  }
  return null;
}

/**
 * The five answers an EDJ quick estimate is built from, as stored on the intake survey. Optional
 * and untyped because they are checked, not trusted: a missing answer is a problem to report.
 */
export interface EdjQuickPlanAnswers {
  clientBirthDate?: unknown;
  clientGenderAtBirth?: unknown;
  clientMaritalStatus?: unknown;
  clientZipCode?: unknown;
  householdTotalAssetsValue?: unknown;
}

/**
 * The first reason these answers cannot produce an EDJ model input, or null when they can.
 *
 * The one gate every EDJ write passes: the model body builder, the app's quick edit, and the
 * conversation / Magic Email edits all check the complete set of answers (stored values with the
 * change applied) before anything is saved, so a saved answer can always be re-run.
 */
export function edjQuickPlanAnswersProblem(answers: EdjQuickPlanAnswers): string | null {
  return (
    edjQuickPlanBirthDateProblem(answers.clientBirthDate) ??
    (isEdjQuickPlanSex(answers.clientGenderAtBirth) ? null : EDJ_QUICK_PLAN_MESSAGES.sex) ??
    (isEdjQuickPlanMaritalCategory(answers.clientMaritalStatus)
      ? null
      : EDJ_QUICK_PLAN_MESSAGES.maritalStatus) ??
    (namedCensusRegionForZipCode(answers.clientZipCode) ? null : EDJ_QUICK_PLAN_MESSAGES.zipCode) ??
    (isEdjQuickPlanNetWorth(answers.householdTotalAssetsValue) ? null : EDJ_QUICK_PLAN_MESSAGES.netWorth)
  );
}

/**
 * The first reason an edit would leave an EDJ quick estimate with answers its model input cannot
 * use, or null when it would not (or the client is not on the EDJ input). `changes` wins over
 * `storedAnswers`, so a null in `changes` is a cleared answer and is refused.
 */
export function edjQuickPlanEditProblem(
  client: Parameters<typeof usesEdjQuickPlanModelInput>[0],
  storedAnswers: EdjQuickPlanAnswers | null | undefined,
  changes: Record<string, unknown>,
): string | null {
  if (!usesEdjQuickPlanModelInput(client)) return null;
  return edjQuickPlanAnswersProblem({ ...storedAnswers, ...changes });
}

/** Quick-edit fields offered only on an EDJ quick estimate, on top of `QUICK_EDIT_ALL_FIELDS`. */
export const EDJ_QUICK_EDIT_EXTRA_FIELDS = ['clientMaritalStatus'] as const;

/** The stored `householdPartnered` answer that goes with an EDJ marital answer. */
export function edjQuickPlanPartneredAnswer(maritalStatus: EdjQuickPlanMaritalCategory): 'Yes' | 'No' {
  return maritalStatus === 'Married' ? 'Yes' : 'No';
}

/** Whether this client was created by the EDJ Quick Plan, read from its creation marker. */
export function isEdjQuickPlanClient(client: { metadata?: Record<string, any> | null }): boolean {
  return client.metadata?.quickPlan?.profile === EDJ_QUICK_PLAN_PROFILE;
}

export function isEdjQuickPlanMaritalCategory(
  value: unknown,
): value is EdjQuickPlanMaritalCategory {
  return EDJ_QUICK_PLAN_MARITAL_CATEGORIES.includes(value as EdjQuickPlanMaritalCategory);
}

export function isEdjQuickPlanNetWorth(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value >= EDJ_QUICK_PLAN_NET_WORTH_MIN &&
    value <= EDJ_QUICK_PLAN_NET_WORTH_MAX
  );
}

/**
 * The census region of a five-digit ZIP, or null when the ZIP does not resolve to one.
 *
 * `zipCodeToState` answers `'Other'` only for its `none` entry, which is what an unrecognised
 * prefix (and the Virgin Islands, Guam and military AE/AP prefixes) falls through to. No real
 * state ZIP produces it, so `'Other'` is treated as "no region" rather than sent to the models.
 */
export function namedCensusRegionForZipCode(zipCode: unknown): NamedCensusRegion | null {
  if (typeof zipCode !== 'string' || !/^\d{5}$/.test(zipCode)) {
    return null;
  }

  const { censusRegion } = zipCodeToState(zipCode);
  return censusRegion === 'Other' ? null : censusRegion;
}

export function isEdjPilotAdvisor(featureFlags: FeatureFlags | undefined | null): boolean {
  return findActivePilotProgram(featureFlags)?.flagKey === 'edjPilotAdvisor';
}

export interface QuickPlanAvailability {
  /**
   * False only for an EDJ advisor without `edjQuickPlan`. True for every other advisor, whose
   * existing pilot restrictions still apply on top of this.
   */
  available: boolean;
  /** The one short-form profile this advisor may submit. */
  profile: ShortFormIntakeProfile;
}

export function resolveQuickPlanAvailability({
  advisorFeatureFlags,
  appConfigFlags,
}: {
  advisorFeatureFlags: FeatureFlags | undefined | null;
  appConfigFlags: AppConfigFeatureFlags | undefined | null;
}): QuickPlanAvailability {
  if (!isEdjPilotAdvisor(advisorFeatureFlags)) {
    return { available: true, profile: STANDARD_SHORT_FORM_PROFILE };
  }

  return {
    available: appConfigFlags?.[EDJ_QUICK_PLAN_FEATURE_FLAG] === true,
    profile: EDJ_QUICK_PLAN_PROFILE,
  };
}

export function isEdjQuickEstimate(
  inferenceSet: Pick<InferenceSet, 'modelInputProfile'> | undefined | null,
): boolean {
  return inferenceSet?.modelInputProfile === EDJ_QUICK_PLAN_PROFILE;
}

/**
 * Whether a re-run of this client's predictions must use the EDJ model input.
 *
 * The latest inference row decides, so the rule stops once a full intake writes a standard row.
 * With no row at all (the Quick Plan's own model run failed), the client's `metadata.quickPlan`
 * decides instead, so a failed first run cannot hand an EDJ survey to the standard body. The
 * server's re-run path and the quick-edit form both use this, so the fields the form offers match
 * the input the server will build.
 */
export function usesEdjQuickPlanModelInput(client: {
  inferenceSet?: Pick<InferenceSet, 'modelInputProfile'> | null;
  metadata?: Record<string, any> | null;
}): boolean {
  return client.inferenceSet ? isEdjQuickEstimate(client.inferenceSet) : isEdjQuickPlanClient(client);
}
